import { t } from "../../../../utils/i18n";
import React, { Component } from "react";
import { Formik, Form } from "formik";
import toastr from "toastr";
import Header from "./components/Header";
import PersonalDetails from "./components/PersonalDetails";
import Terms from "./components/Terms";
import MailingDetails from "./components/MailingDetails";
import Preview from "./components/Preview";
import { postRequest } from "../../../../utils/httpRequest";
import { formErrors } from "../../../../utils/formErrors";
import {
  cardRequestParams,
  formattedApiErrors,
  stepFromError,
} from "./helpers";
import { CardType } from "./Types";
import "./index.scss";

import moment from "moment";

interface Props {
  params: {
    availableBalance: number;
    physicalCardTypes: CardType[];
    virtualCardTypes: CardType[];
    couponCode: string;
    existingCardRequest: {
      name_on_card: string;
      address: string;
      city: string;
      state: string;
      country: string;
      postal_code: string;
    };
    existingCardAccount: {
      phone_code: string;
      phone_number: string;
      occupation: string;
      annual_salary: string;
      account_purpose: string;
      date_of_birth: string;
      expected_monthly_volume: string;
    };
    user: {
      id: string;
      firstName: string;
      lastName: string;
      isQAUser: boolean;
      dateOfBirth: string;
    };
  };

  isMultiRequest: boolean;
}

interface State {
  step: "cardType" | "personal" | "mailing" | "terms" | "preview";
  cardType: string;
  couponCode: string;
  countryCode: string;
  submitting: boolean;
  isCouponClaimable: boolean;
  isLoading: boolean;
  apiErrors?: any;
}

class VirtualCard extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      step: "personal",
      cardType: "virtual",
      couponCode: props.params?.couponCode || "",
      submitting: false,
      isCouponClaimable: false,
      isLoading: false,
      countryCode: "",
      apiErrors: {},
    };
  }

  componentDidMount() {
    const { virtualCardTypes, couponCode } = this.props.params;

    postRequest("/api/v1/cards/coupon_claimabilities", {
      coupon_code: couponCode,
      card_type: "virtual",
    })
      .then((response) => {
        const cardType = virtualCardTypes.find(
          (card) => card.type === "virtual"
        );

        cardType.discounted_fee = response.discounted_fee;
        cardType.coupon_code = response.coupon_code;
        cardType.is_coupon_claimable = true;
      })
      .catch((error) => {
        console.error("Error fetching coupon:", error);
      });
  }

  handlePreviousStepChange = (step) => {
    this.setState({ step });
  };

  handleSetStep = (step) => {
    this.setState({ step });
  };
  handleNextStepChange = (validateForm, step) => {
    validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        window.scrollTo(0, 0);
        this.setState({ step });
      }
    });
  };

  updateCouponCode = (couponCode) => {
    this.setState({ couponCode });
  };

  updateCountryCode = (countryCode) => {
    this.setState({ countryCode });
  };

  onSelectCardType = (e, cardType) => {
    e.preventDefault();

    this.setState({ cardType });

    if (cardType === "virtual") {
      this.setState({ isLoading: true });
      window.location.href = "/cards/requests/virtual";
    } else {
      this.handleSetStep("personal");
    }
  };

  confirmSubmission = (values) => {
    if (!values.passport_bio_page) {
      this.handleSetStep("personal");
      toastr.error(t("cards.request_form.upload_all_documents"));
    } else {
      this.submit(values);
    }
  };

  submit = (values) => {
    const { cardType, isCouponClaimable, couponCode, countryCode } = this.state;
    this.setState({ submitting: true });

    const params = cardRequestParams(cardType, countryCode, values);

    const formData = new FormData();
    if (couponCode) {
      formData.append("coupon_code", couponCode);
    }

    Object.entries(params).forEach(([key, value]) => {
      formData.append(`card_account[${key}]`, value);
    });

    const url = "/api/v1/cards/users/create_rain_account.json";

    postRequest(url, formData, {
      "Content-Type": "multipart/form-data",
    })
      .then((response) => {
        if (
          response.completion_link &&
          response.status === "needs_verification"
        ) {
          window.location.href = `${response.completion_link}`;
        } else {
          window.location.href = `/cards/requests/${response.card_request_id}`;
        }
      })
      .catch((error) => {
        console.log(error);
        this.setState({ submitting: false });

        if (error.data?.errors) {
          const errors = formErrors(error.data.errors);

          this.setState({
            apiErrors: formattedApiErrors(errors),
            step: stepFromError(error.data.errors),
          });
        }

        toastr.error(
          error?.error || error?.data?.error || t("cards.common.something_went_wrong")
        );
      });
  };

  render() {
    const {
      params: {
        user,
        availableBalance,
        physicalCardTypes,
        virtualCardTypes,
        existingCardRequest,
        existingCardAccount,
      },
      isMultiRequest,
    } = this.props;

    const { isLoading, step, countryCode, cardType, submitting, apiErrors } =
      this.state;

    return (
      <div className="w-100">
        {isLoading ? (
          <div className="spinner-border" role="status">
            <span className="sr-only">{t("cards.request_form.loading")}</span>
          </div>
        ) : (
          <>
            <div className="create-offer-title">
              <h2 className="create-trade-page-title ml-3 mt-3">
                <i className="fa fa-credit-card"></i> {t("cards.request_form.apply_for_a_card")}
              </h2>
            </div>
            <Formik
              initialValues={{
                first_name: user.firstName,
                last_name: user.lastName,
                middle_name: "",
                name_on_card: existingCardRequest
                  ? existingCardRequest.name_on_card
                  : `${user.firstName} ${user.lastName}`,
                occupation: existingCardAccount?.occupation || "",
                phone_code: existingCardAccount?.phone_code || "",
                phone_number: existingCardAccount?.phone_number || "",
                annual_salary: existingCardAccount?.annual_salary || "",
                account_purpose: existingCardAccount?.account_purpose || "",
                expected_monthly_volume:
                  existingCardAccount?.expected_monthly_volume || "",
                date_of_birth: "",
                address: existingCardRequest?.address || "",
                city: existingCardRequest?.city || "",
                state: existingCardRequest?.state || "",
                country: existingCardRequest?.country || "",
                postal_code: existingCardRequest?.postal_code || "",
                passport_bio_page: "",
                authorized_payment_accepted: false,
              }}
              onSubmit={(values) => this.confirmSubmission(values)}
            >
              {({
                setFieldValue,
                errors,
                values,
                handleChange,
                validateForm,
              }) => (
                <Form className="form">
                  <div className="steps card shadow-sm rounded-lg">
                    <div>
                      {step !== "cardType" && (
                        <div
                          className="col-md-12 mb-10"
                          style={{ marginBottom: "20px" }}
                        >
                          <Header
                            step={step}
                            handleSetStep={this.handleSetStep}
                            isVirtualCardRequest={true}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      {step === "personal" && (
                        <PersonalDetails
                          errors={errors}
                          values={values}
                          dateOfBirth={existingCardAccount?.date_of_birth}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                          isVirtualCardRequest={true}
                        />
                      )}
                      {step === "mailing" && (
                        <MailingDetails
                          errors={errors}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          selectedCountry={values.country}
                          isVirtualCardRequest={true}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                          updateCountryCode={this.updateCountryCode}
                          isMultiRequest={isMultiRequest}
                        />
                      )}
                      {step === "terms" && (
                        <Terms
                          countryCode={countryCode}
                          errors={errors}
                          apiErrors={apiErrors}
                          setFieldValue={setFieldValue}
                          validateForm={validateForm as any}
                          handleNextStepChange={this.handleNextStepChange}
                          handlePreviousStepChange={
                            this.handlePreviousStepChange
                          }
                        />
                      )}
                      {step === "preview" && (
                        <Preview
                          data={values}
                          handleSetStep={this.handleSetStep}
                          cardType={cardType}
                          physicalCardTypes={physicalCardTypes}
                          virtualCardTypes={virtualCardTypes}
                          submitting={submitting}
                          isMultiRequest={isMultiRequest}
                        />
                      )}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        )}
      </div>
    );
  }
}

export default VirtualCard;
